// React
import React, { startTransition, useEffect, useState } from 'react';

// React Router
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// CoreUI Components
import { CFormCheck, CRow } from '@coreui/react';

// Custom Modals
import EmailBrandNewModal from 'src/components/Modals/EmailBrandNewModal';
import TermsAndConditionModal from 'src/components/Modals/TermsAndConditionModal';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';

// Helper Functions
import { getDaAppllyInputs, getInitialDaData } from 'src/configs/InputConfig/addDAFormConfig';
import { formValidator } from 'src/helpers/formValidator';
import { generateRandomPassword } from 'src/helpers/generatePassowrd';

// UI Components
import { useSelector } from 'react-redux';
import DataGridHeader from 'src/components/DataGridComponents/DataGridHeader';
import Inputs from 'src/components/Filters/Inputs';
import AppContainer from 'src/components/UI/AppContainer';
import Button from 'src/components/UI/Button';
import Form from 'src/components/UI/Form';
import Loading from 'src/components/UI/Loading';

// Custom Hooks
import moment from 'moment';
import { useFetchDsps } from 'src/hooks/api/useFetchDsps';
import { useUpdateUser } from 'src/hooks/api/useUpdateUser';
import { useUserAvailability } from 'src/hooks/api/useUserAvailability';
import useEmailVerification from 'src/hooks/useEmailVerification';
import { useShowSnackbar } from 'src/hooks/useShowSnackbar';
import { generateOfferLetter } from 'src/reports/pdf/daOfferLetter';

const AddDA = () => {
  useEffect(() => {
    fetchDspList();
  }, []);

  const navigate = useNavigate();
  const showSnackbar = useShowSnackbar();
  const { dsp } = useParams();

  const { checkEmailValidation } = useEmailVerification();
  const { loading: dspLoading, fetchDsp } = useFetchDsps();
  const { createUpdateUser, loading: createUpdateUserLoading } = useUpdateUser();

  const { checkUserAvailability } = useUserAvailability();

  const [daApplyFormData, setDaApplyFormData] = useState(getInitialDaData());
  const [isThisBrandnew, setIsThisBrandnew] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [termsmodalOpen, setTermsmodalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [emailReadonly, setEmailReadonly] = useState(true);
  const [emailMessage, setEmailMessage] = useState('Enter Valid Email Address');
  const [dspList, setDspList] = useState([]);

  const user = useSelector((state) => state.user);
  const lastVerifiedEmailRef = useRef('');
  const lastVerificationStatusRef = useRef(''); // "valid" | "invalid" | "associated"

  useEffect(() => {
    if (dspList.length == 0 || !dsp) return;
    handleDAFormData({ target: { name: 'dspid', value: dsp } });
  }, [dspList, dsp]);

  const handleDAFormData = (event, label = '') => {
    if (label == 'dob') {
      setDaApplyFormData((prevdaApplyFormData) => ({
        ...prevdaApplyFormData,
        [label]: moment(event).utc().format(),
      }));
    } else {
      const { name, value, type, checked } = event.target;
      setDaApplyFormData((prevdaApplyFormData) => ({
        ...prevdaApplyFormData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };
  // 🔹 Handle email input changes
  const handleEmailChange = (e) => {
    const newEmail = e.target.value.trim();

    setDaApplyFormData((prev) => ({
      ...prev,
      email: newEmail,
    }));

    // 🔹 Show modal only when new valid email entered
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})?$/;

    if (emailRegex.test(newEmail)) {
      setIsThisBrandnew(true);
      toggleModal(true); // open modal immediately
    }
  };

  const onBlur = async () => {
    const fieldValue = daApplyFormData.email?.trim();
    const emailInputElement = document.getElementById('email');

    // 1️⃣ Empty field check
    if (!fieldValue) {
      setEmailMessage('');
      emailInputElement.setCustomValidity('');
      lastVerifiedEmailRef.current = '';
      lastVerificationStatusRef.current = '';
      return;
    }

    // 2️⃣ Skip re-verification if already valid/associated/processing
    if (
      lastVerifiedEmailRef.current === fieldValue &&
      ['valid', 'associated', 'processing'].includes(lastVerificationStatusRef.current)
    ) {
      return;
    }

    // 3️⃣ Regex check
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})?$/;

    if (!emailRegex.test(fieldValue)) {
      const msg = `${fieldValue} is not a valid email`;
      showSnackbar(msg, 'error');
      setEmailMessage(msg);
      emailInputElement.setCustomValidity(msg);
      lastVerifiedEmailRef.current = fieldValue;
      lastVerificationStatusRef.current = 'invalid';
      return;
    }
    if (lastVerifiedEmailRef.current !== fieldValue) {
      handleFocus();
    }
  };
  const emailValidatorFn = async () => {
    const fieldValue = daApplyFormData.email?.trim();
    const emailInputElement = document.getElementById('email');

    emailInputElement.setCustomValidity('');
    lastVerifiedEmailRef.current = fieldValue;
    lastVerificationStatusRef.current = 'processing';

    const isValidEmail = await checkEmailValidation(fieldValue);

    if (isValidEmail !== null && isValidEmail !== 'valid') {
      const msg = `${fieldValue} is not a valid email`;
      showSnackbar(msg, 'error');
      setEmailMessage(msg);
      emailInputElement.setCustomValidity(msg);
      lastVerificationStatusRef.current = 'invalid';
      return;
    }

    const resultMsg = await new Promise((resolve) => {
      checkUserAvailability(fieldValue, '', daApplyFormData.id, (msg) => resolve(msg));
    });

    if (resultMsg && resultMsg.toLowerCase().includes('already associated')) {
      showSnackbar(resultMsg, 'warning');
      setEmailMessage(resultMsg);
      setDaApplyFormData((prev) => ({ ...prev, email: '' }));
      if (emailInputElement) emailInputElement.value = '';
      lastVerificationStatusRef.current = 'associated';
    } else {
      setEmailMessage(resultMsg || '');
      lastVerificationStatusRef.current = 'valid';
    }
  };

  const submitDA = async () => {
    const selectedDsp = dspList?.find((dsp) => dsp.id == daApplyFormData.dspid);

    formValidator();
    const form = document.querySelector('.apply-da-form');
    if (daApplyFormData.email === '') {
      setEmailReadonly(false);
      return;
    }
    if (!daApplyFormData.dob) {
      showSnackbar('Please select Date of Birth', 'warning');
      return;
    }
    if (form.checkValidity()) {
      const daBody = {
        ...daApplyFormData,
        secondaryContact: daApplyFormData.isWhatsAppAsso ? daApplyFormData.primaryContact : '',
        address: '',
        dspName: user.dspConfig.dspName,
        userName: daApplyFormData.userName ? daApplyFormData.userName : daApplyFormData.email,
        password: daApplyFormData.password ? daApplyFormData.password : generateRandomPassword(12),
        roleId: daApplyFormData.positionType ?? 0,
        rowVer: 0,
        signature: daApplyFormData.signature,
        OfferTypeId: daApplyFormData.offerType,
        SuffixId: daApplyFormData.suffix,
        remarks: 'created da',
      };

      try {
        const res = await createUpdateUser(daBody);
        if (res?.status === true) {
          generateOfferLetter(selectedDsp, daApplyFormData);
          navigate('/');
        } else if (res && res?.message && res?.message?.includes('sending mail.')) {
          generateOfferLetter(selectedDsp, daApplyFormData);
          navigate('/');
          showSnackbar('Acount has been created successfully, email sending failed', 'success');
        } else {
          showSnackbar(res?.message, 'error');
        }
      } catch (error) {
        showSnackbar(error?.message, 'error');
      }
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const TermsModal = () => {
    setTermsmodalOpen(!termsmodalOpen);
  };

  const confirmationModal = () => {
    setConfirmationModalOpen(!confirmationModalOpen);
  };
  const goToAnotherPage = () => {
    startTransition(() => {
      navigate(-1);
    });
  };

  const IsThisBrandNewClick = () => {
    setIsThisBrandnew(!isThisBrandnew);
  };
  const associatedWithAmazonNo = () => {
    emailValidatorFn();
    toggleModal();
    setEmailReadonly(false);
  };

  const associatedWithAmazon = () => {
    const emailInputElementVal = document.getElementById('email');

    // 🧹 1. Clear the actual input value (visual)
    if (emailInputElementVal) {
      emailInputElementVal.value = '';
    }

    // 🧹 2. Clear React state (this is what truly drives the input)
    setDaApplyFormData((prev) => ({
      ...prev,
      email: '', // ✅ clear email from state
    }));

    // 🧹 3. Reset validation refs (to avoid skipping next blur verification)
    lastVerifiedEmailRef.current = '';
    lastVerificationStatusRef.current = '';

    // 🧹 4. Close modal and set readonly
    toggleModal();
    setEmailReadonly(true);
  };

  const handleFocus = () => {
    toggleModal(true);
    setIsThisBrandnew(true);
  };

  const fetchDspList = async () => {
    const dspData = await fetchDsp();
    setDspList(dspData);
  };

  const daApplyInputs = getDaAppllyInputs(
    daApplyFormData,
    handleDAFormData,
    handleEmailChange, // 🔹 use this instead
    // handleFocus,
    emailReadonly,
    emailMessage,
    onBlur,
    dspList,
    dsp,
  );

  return (
    <React.Fragment>
      {dspLoading && <Loading />}
      <AppContainer>
        <DataGridHeader filterDisable={true} />
        <React.Fragment>
          <div style={{ textAlign: 'center' }}>
            <img
              className="LogoStyl mb-2"
              src="4DSPS_White.png"
              alt="logo"
              style={{ width: '150px', display: 'inline-block' }}
            />
          </div>

          <Form name="apply-da-form">
            <Inputs inputFields={daApplyInputs} isBtn={false} />

            {/* 🚫 These are OUTSIDE the disabled block — always clickable */}
            <CFormCheck
              className="mt-3 d-flex flex-row justify-content-center"
              title="Are you agree for this?"
              label={
                <span>
                  By providing this info, you agree to our terms & conditions, read our{' '}
                  <strong className="lblTerms" onClick={TermsModal}>
                    Terms & Conditions (EULA)
                  </strong>
                </span>
              }
              name="isTermsAccepted"
              id="isTermsAccepted"
              required
              checked={daApplyFormData.isTermsAccepted}
              onChange={handleDAFormData}
            />
            <CRow className="CenterAlign gap-3 mb-2">
              <Button
                title="Cancel"
                onClick={confirmationModal}
                disabled={createUpdateUserLoading}
              />
              <Button
                title="Submit"
                type="submit"
                onClick={submitDA}
                disabled={createUpdateUserLoading}
                loading={createUpdateUserLoading}
              />
            </CRow>
          </Form>
        </React.Fragment>

        <ConfirmationModal
          header="Confirmation!"
          body="Are you sure you want to cancel?"
          isOpen={confirmationModalOpen}
          onYes={goToAnotherPage}
          onNo={confirmationModal}
        />
      </AppContainer>
      <EmailBrandNewModal
        isOpen={modalOpen}
        toggle={toggleModal}
        isThisBrandnew={isThisBrandnew}
        IsThisBrandNewClick={IsThisBrandNewClick}
        associatedWithAmazonNo={associatedWithAmazonNo}
        associatedWithAmazon={associatedWithAmazon}
      />
      <TermsAndConditionModal isOpen={termsmodalOpen} toggle={TermsModal} />
    </React.Fragment>
  );
};

export default AddDA;
